import React, { useEffect, useState } from "react";
import {
  getKPIs,
  getActivity,
  getUserGrowth,
  getJobCategories,
  getTopPerformers,
  getRecentActivity
} from "../../adminAPI/adminAnalyticsApi";
import Chart from "react-apexcharts";

const AnalyticsDashboard = () => {
  const token = localStorage.getItem("otoken");

  /* ================= STATE ================= */
  const [kpis, setKpis] = useState({});
  const [activity, setActivity] = useState([]);
  const [growth, setGrowth] = useState([]);
  const [categories, setCategories] = useState([]);
  const [performers, setPerformers] = useState([]);
  const [recent, setRecent] = useState([]);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
  const load = async () => {
    try {
      console.log("Analytics load started");
      
      const results = await Promise.all([
        getKPIs(token),
        getActivity(token),
        getUserGrowth(token),
        getJobCategories(token),
        getTopPerformers(token),
        getRecentActivity(token)
      ]);

      const [
        kpiRes,
        activityRes,
        growthRes,
        categoryRes,
        performerRes,
        recentRes
      ] = results;

      console.log("RAW activity API response:", activityRes);
      console.log("Activity data only:", activityRes.data);
      console.log("Activity length:", activityRes.data?.length);

      setKpis(kpiRes.data);
      setActivity(activityRes.data);
      setGrowth(growthRes.data);
      setCategories(categoryRes.data);
      setPerformers(performerRes.data);
      setRecent(recentRes.data);

    } catch (err) {
      console.error("❌ Analytics load failed:", err);
      console.error("Axios error response:", err?.response);
    }
  };

  load();
}, []);


  /* ================= ACTIVITY CHART ================= */

  const activityCategories = activity.map(i => i.day);

  const usersSeries = activity.map(i => Number(i.users));
  const jobsSeries = activity.map(i => Number(i.jobs));
  const applicationsSeries = activity.map(i => Number(i.applications));

  const activityChart = {
    series: [
      { name: "Users", data: usersSeries },
      { name: "Jobs", data: jobsSeries },
      { name: "Applications", data: applicationsSeries }
    ],
    options: {
      chart: {
        type: "area",
        height: 350,
        toolbar: { show: false }
      },
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 2 },
      xaxis: {
        categories: activityCategories
      },
      colors: ["#0d6efd", "#198754", "#ffc107"],
      legend: { position: "top" }
    }
  };



  /* ================= USER GROWTH ================= */
  const growthChart = {
    series: [{ name: "New Users", data: growth.map(x => x.value) }],
    options: {
      chart: { type: "bar", height: 350 },
      plotOptions: { bar: { borderRadius: 4 } },
      xaxis: { categories: growth.map(x => x.label) },
      colors: ["#0d6efd"]
    }
  };

  /* ================= CATEGORY ================= */
  const categoryChart = {
    series: categories.map(x => x.value),
    options: {
      labels: categories.map(x => x.label),
      chart: { type: "donut", height: 350 },
      legend: { position: "bottom" }
    }
  };

  return (
    <main className="container-fluid mt-5 pt-5">

      {/* KPI CARDS */}
      <div className="row g-4 mb-4">
        <StatCard title="Total Users" value={kpis.total_users} icon="bi-people" bg="primary" />
        <StatCard title="Active Jobs" value={kpis.active_jobs} icon="bi-briefcase" bg="success" />
        <StatCard title="Applications" value={kpis.applications} icon="bi-file-earmark-text" bg="warning" />
        <StatCard title="Revenue" value={`₹${kpis.revenue}`} icon="bi-currency-dollar" bg="info" />
      </div>

      {/* CHARTS */}
      <div className="row g-4">
        <div className="col-xl-8">
          {activity.length > 0 ? (
            <Chart
              options={activityChart.options}
              series={activityChart.series}
              type="area"
              height={400}
            />
          ) : (
            <div className="text-center text-muted py-5">
              No data to render activity graph
            </div>
          )}
        </div>
        <div className="col-xl-4">
          <TopPerformers data={performers} />
        </div>
      </div>

      <div className="row g-4 mt-4">
        <div className="col-lg-6">
          <Chart {...growthChart} type="bar" height={350} />
        </div>
        <div className="col-lg-6">
          <Chart {...categoryChart} type="donut" height={350} />
        </div>
      </div>

      <RecentActivity data={recent} />
    </main>
  );
};

/* ================= COMPONENTS ================= */

const StatCard = ({ title, value, icon, bg }) => (
  <div className="col-xl-3 col-md-6">
    <div className={`card bg-${bg} text-white shadow-sm border-0`}>
      <div className="card-body d-flex justify-content-between">
        <div>
          <h6>{title}</h6>
          <h2>{value || 0}</h2>
        </div>
        <i className={`bi ${icon} fs-1 opacity-75`} />
      </div>
    </div>
  </div>
);

const TopPerformers = ({ data }) => (
  <div className="card shadow-sm border-0 h-100">
    <div className="card-body">
      <h6>Top Performers</h6>
      {data.map((p, i) => (
        <div key={i} className="mb-3">
          <strong>{p.company}</strong>
          <small className="text-muted d-block">{p.applications} Applications</small>
          <div className="progress mt-1">
            <div className="progress-bar" style={{ width: `${p.percent}%` }} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const RecentActivity = ({ data = [] }) => (
  <div className="card shadow-sm border-0 mt-4">
    <div className="card-body">
      <h6>Recent Activity</h6>

      {data.length === 0 ? (
        <div className="text-muted text-center py-4">
          No recent activity
        </div>
      ) : (
        <table className="table">
          <tbody>
            {data.map((r, i) => (
              <tr key={i}>
                <td>
                  <strong>{r.event}</strong> by<br />
                  <small className="text-muted">{r.user}</small>
                </td>
                <td className="text-muted">
                  {new Date(r.created_at).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
);


export default AnalyticsDashboard;
